import React from "react";
import { Button, Tooltip, Spin } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import LiquidGlass from "./LiquidGlass";

function normalizeUuid(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  // eslint-disable-next-line eqeqeq
  if (!s || s == "1") return null;// make it == because it can be string vs number. do not change to ===
  return s;
}

function getPlanDisplayName(plan) {
  if (!plan) return null;
  return plan.display_name || plan.name || (plan.uuid != null ? String(plan.uuid) : null);
}

function getPlanTss(plan) {
  if (!plan) return null;
  const raw = plan.stress_points ?? plan.tss ?? plan.TSS ?? null;
  if (raw === null || raw === undefined || raw === "") return null;
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  return Math.round(n);
}

function formatPlanNameWithTss({ planName, selectedPlan }) {
  const base = String(planName || getPlanDisplayName(selectedPlan) || "-");
  const tss = getPlanTss(selectedPlan);
  if (!base || base === "-" || tss === null) return base;
  return `${base} (TSS : ${tss})`;
}

function categoryLabelWithStats({ plansInfo, category }) {
  const catKey = String(category || "").trim();
  if (!catKey) return "-";

  const plans = (plansInfo || []).filter((p) => String(p?.category || "").trim() === catKey);
  const x = plans.length;
  if (x <= 0) return catKey;

  const weekRe = /week\s*([0-9]{1,3})/i;
  let weekMentionCount = 0;
  let maxWeek = null;

  plans.forEach((p) => {
    const name = String(p?.name || "").trim() || String(p?.display_name || "").trim();
    if (!name) return;
    if (/week/i.test(name)) weekMentionCount += 1;
    const m = name.match(weekRe);
    if (m && m[1] != null) {
      const n = Number(m[1]);
      if (!Number.isNaN(n)) maxWeek = maxWeek === null ? n : Math.max(maxWeek, n);
    }
  });

  const hasWeeks = weekMentionCount >= Math.ceil(x / 2) && maxWeek !== null;
  const plansLabel = `${x} plan${x === 1 ? "" : "s"}`;
  const weeksLabel = `${maxWeek} week${maxWeek === 1 ? "" : "s"}`;
  const suffix = hasWeeks ? `(${weeksLabel}, ${plansLabel})` : `(${plansLabel})`;
  return `${catKey} ${suffix}`;
}

export default function PlanSetupProgramNavigation({
  username,
  selectedPlan,
  planName,
  planCategory,
  previousUuid,
  plansInfo,
  selectPlan,
  getAllTrainingPlansInfo,
  getPlanLabelByUuid,
  showTrainingForm,
  isMobile = false,
  isSelectingPlan = false,
}) {
  const currentUuid = normalizeUuid(selectedPlan?.uuid);
  const currentPlanUsername = selectedPlan?.username || username || "";
  const currentCategory = planCategory || selectedPlan?.category || "";
  const planNameWithTss = React.useMemo(() => {
    return formatPlanNameWithTss({ planName, selectedPlan });
  }, [planName, selectedPlan]);
  const currentCategoryLabel = React.useMemo(() => {
    return categoryLabelWithStats({ plansInfo, category: currentCategory });
  }, [plansInfo, currentCategory]);

  const prevUuid = normalizeUuid(previousUuid ?? selectedPlan?.previous_uuid);

  const prevPlan = React.useMemo(() => {
    if (!prevUuid) return null;
    return (plansInfo || []).find((p) => normalizeUuid(p?.uuid) === prevUuid) || null;
  }, [plansInfo, prevUuid]);

  const [externalPrevLabel, setExternalPrevLabel] = React.useState(null);
  React.useEffect(() => {
    setExternalPrevLabel(null);
    if (!prevUuid) return;
    if (prevPlan) return;
    if (typeof getPlanLabelByUuid !== "function") return;
    let cancelled = false;
    (async () => {
      try {
        const lbl = await getPlanLabelByUuid(prevUuid);
        if (!cancelled) setExternalPrevLabel(lbl || null);
      } catch {
        if (!cancelled) setExternalPrevLabel(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [prevUuid, prevPlan, getPlanLabelByUuid]);

  const prevPlanLabel = getPlanDisplayName(prevPlan) || externalPrevLabel || prevUuid;

  const nextPlan = React.useMemo(() => {
    if (!currentUuid) return null;
    const all = (plansInfo || []).filter((p) => normalizeUuid(p?.uuid));
    const candidates = all.filter((p) => normalizeUuid(p?.previous_uuid) === currentUuid);
    if (!candidates.length) return null;

    const catKey = String(currentCategory || "").trim();
    // If we have a category, always prefer next plans inside the same category/program.
    // If not, don't accidentally treat "empty category" as a program and over-filter.
    const sameCategory = catKey
      ? candidates.filter((p) => String(p?.category || "").trim() === catKey)
      : [];
    const pool = sameCategory.length > 0 ? sameCategory : candidates;

    return [...pool].sort((a, b) => {
      const an = String(getPlanDisplayName(a) || "");
      const bn = String(getPlanDisplayName(b) || "");
      return an.localeCompare(bn);
    })[0];
  }, [plansInfo, currentUuid, currentCategory]);

  const nextUuid = normalizeUuid(nextPlan?.uuid);
  const nextPlanLabel = getPlanDisplayName(nextPlan) || nextUuid;

  const canGoPrev = Boolean(prevUuid);
  const canGoNext = Boolean(nextUuid);
  const navDisabled = Boolean(isSelectingPlan);

  const goToUuid = async (uuid) => {
    const u = normalizeUuid(uuid);
    if (!u) return;
    const inList = (plansInfo || []).some((p) => normalizeUuid(p?.uuid) === u);
    if (inList) {
      if (typeof selectPlan === "function") {
        await selectPlan(u, plansInfo);
      }
      return;
    }
    // Fallback: refresh plans, then auto-select via App.js' getAllTrainingPlansInfo -> selectPlan(uuid)
    getAllTrainingPlansInfo && getAllTrainingPlansInfo({ uuid: u });
  };

  // Hide if we're editing/creating, or if no plan is selected.
  if (showTrainingForm || !currentUuid) return null;

  // While plan steps are loading, show a minimal header only (no category/username/arrows)
  if (isSelectingPlan) {
    return (
      <LiquidGlass
        className="liquid-glass"
        style={{
          marginBottom: 12,
          padding: 16,
          overflow: "hidden",
        }}
      >
        {/* Keep the same height/layout as the normal nav, but show placeholders */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            marginBottom: 10,
          }}
        >
          <div style={{ flex: "0 0 70%", minWidth: 0, textAlign: "left", paddingRight: 12 }}>
            <div style={{ fontSize: 12, opacity: 0.75 }}>Program / Category</div>
            <div
              style={{
                fontWeight: 700,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                paddingLeft: 2,
              }}
              title="-"
            >
              -
            </div>
          </div>
          <div
            style={{
              flex: "0 0 30%",
              minWidth: 0,
              textAlign: "right",
              paddingRight: 12,
              marginLeft: isMobile ? -24 : 0,
            }}
          >
            <div style={{ fontSize: 12, opacity: 0.75 }}>Username</div>
            <div
              style={{
                fontWeight: 700,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title="-"
            >
              -
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 12 }}>
          {/* Keep arrows visible but disabled while loading */}
          <div style={{ flex: "0 0 10%", display: "flex", justifyContent: "flex-start" }}>
            <Button aria-label="Previous plan" icon={<LeftOutlined />} disabled />
          </div>
          <div
            style={{
              flex: "0 0 80%",
              textAlign: "center",
              fontWeight: 800,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            title="Loading…"
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Spin size="small" />
              <span>Loading…</span>
            </span>
          </div>
          <div style={{ flex: "0 0 10%", display: "flex", justifyContent: "flex-end" }}>
            <Button aria-label="Next plan" icon={<RightOutlined />} disabled style={{ paddingInline: 10, marginLeft: 4 }} />
          </div>
        </div>
      </LiquidGlass>
    );
  }

  return (
    <LiquidGlass
      className="liquid-glass"
      style={{
        marginBottom: 12,
        padding: 16,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 10,
        }}
      >
        <div style={{ flex: "0 0 70%", minWidth: 0, textAlign: "left", paddingRight: 12 }}>
          <div style={{ fontSize: 12, opacity: 0.75 }}>Program / Category</div>
          <div
            style={{
              fontWeight: 700,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              paddingLeft: 2, // prevents “cropped” look against the glass edge
            }}
          >
            {currentCategoryLabel}
          </div>
        </div>
        <div
          style={{
            flex: "0 0 30%",
            minWidth: 0,
            textAlign: "right",
            // Mobile: push username left more (prevents cropping in modal)
            paddingRight:  12,
            marginLeft: isMobile ? -24 : 0,
          }}
        >
          <div style={{ fontSize: 12, opacity: 0.75 }}>Username</div>
          <div
            style={{
              fontWeight: 700,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {currentPlanUsername || "-"}
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, paddingRight: 12 }}>
        <div style={{ flex: "0 0 10%", display: "flex", justifyContent: "flex-start" }}>
          <Tooltip title={canGoPrev ? prevPlanLabel : ""}>
            <Button
              aria-label="Previous plan"
              icon={<LeftOutlined />}
              disabled={!canGoPrev || navDisabled}
              onClick={() => goToUuid(prevUuid)}
            />
          </Tooltip>
        </div>

        <div
          style={{
            flex: "0 0 80%",
            textAlign: "center",
            fontWeight: 800,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={isSelectingPlan ? "Loading…" : planNameWithTss || ""}
        >
          {isSelectingPlan ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Spin size="small" />
              <span>Loading…</span>
            </span>
          ) : (
            planNameWithTss
          )}
        </div>

        <div
          style={{
            flex: "0 0 10%",
            display: "flex",
            justifyContent: "flex-end",
            // Mobile: some extra room, but less than username (per request)
          }}
        >
          <Tooltip title={canGoNext ? nextPlanLabel : ""}>
            <Button
              aria-label="Next plan"
              icon={<RightOutlined />}
              disabled={!canGoNext || navDisabled}
              style={{
                paddingInline: 10,
                marginLeft: 4,
              }}
              onClick={() => goToUuid(nextUuid)}
            />
          </Tooltip>
        </div>
      </div>
    </LiquidGlass>
  );
}


